import React, {useCallback, useEffect, useRef, useState} from 'react';
import useScrollable from 'hooks/useScrollable';
import 'components/UserTable/table.scss';
import Row from 'components/UserTable/Row';
import {useContextMenu} from 'react-contexify';
import ContextMenu from 'components/ContextMenu';
import {insertInsideArray} from 'helpers';

export default ({
  team,
  index,
  tableData,
  items,
  setItems,
  setInitalItemProps,
  moveUp,
  moveDown,
  contextData,
  setContextData,
  createUpdateItem,
  getItems,
  menuId,
  targetRole,
  connectedMethods,
  createUpdateStatus,
}) => {
  const table = useRef();
  const tableHeaderRow = useRef();
  const tableRows = useRef([]);
  const [selectedItem, setSelectedItem] = useState(null);
  // const [isUsersFetched, setIsUsersFetched] = useState(false)
  const {show} = useContextMenu({
    id: menuId,
  });

  // Make table scrollable by dragging the click.
  useScrollable(table, tableData.scrollToEnd);

  // Sometimes the content width of the cell is much bigger than given in the
  // config file, so the next element left position will be less than expected.
  // so we need to recalculate the cell left position if it's sticky.
  useEffect(() => {
    tableRows.current = tableRows.current.slice(0, items.length);
    if (tableRows.current.length) {
      let allTime = 0;
      Array.from(tableRows.current).forEach((row, i) => {
        const start = window.performance.now();
        reCalculatePositionOfStickyHeaders(Array.from(row.children));
        const end = window.performance.now();
        allTime += end - start;
      });
      const headerTableData = Array.from(tableHeaderRow.current.children);
      reCalculatePositionOfStickyHeaders(headerTableData);
      // make scroll left the most possible
      if (tableData.scrollToEnd !== false) {
        table.current.scrollLeft = table.current.offsetWidth;
      }
      // setIsUsersFetched(true)
    }
  }, [items]);

  const reCalculatePositionOfStickyHeaders = useCallback((headerTableData) => {
    let totalWidths = 0;
    headerTableData.forEach((child, i) => {
      const nextChild = headerTableData[i + 1];
      if (
        child.style.position === 'sticky' &&
        nextChild.style.position === 'sticky'
      ) {
        totalWidths += child.offsetWidth;
        nextChild.style.left = `${totalWidths}px`;
      }
    });
  });

  //   calculate sticky headers position to left
  //   start from 0, first item will "position: 0px"
  //   then will follow by the width if previous elements
  //   if first header width = 60px, so the second header position = 60px;
  //   third header will be equal (first item width + second item width) and so on.

  const calculatePositionOfStickyHeaders = useCallback((header) => {
    let left = 0;
    let zIndex = 20;
    if (header.sticky) {
      for (let i = 0; i < header.index; i++) {
        const element = getConfig(i);
        left += element.style.minWidth ?? element.style.width;
        zIndex--;
      }
      const style = {
        ...header.style,
        position: 'sticky',
        left,
        zIndex,
      };
      return style;
    }
    return null;
  }, []);

  const getConfig = useCallback(
    (i) => tableData.headers[Object.keys(tableData.headers)[i]],
    []
  );

  // Calculate the style of thead => tr => td
  const headerTdStyle = useCallback(
    (header) => ({
      ...calculatePositionOfStickyHeaders(header),
      ...header.style,
    }),
    []
  );

  const handleRowClick = useCallback((item) => {
    setSelectedItem((value) => {
      if (value === item) {
        return null;
      }
      return item;
    });
  }, []);

  const handleContextMenu = useCallback((event, data) => {
    setContextData(data);
    show(event);
  }, []);

  const addNewMember = useCallback(() => {
    const item = setInitalItemProps('new_user');
    const newItems = insertInsideArray(
      [...items].filter((f) => f._id !== 'new_user'),
      contextData.index + 1,
      item
    );
    setItems(newItems);
    setSelectedItem(item);
  }, [items, contextData?.index]);

  // eslint-disable-next-line no-unused-expressions
  connectedMethods && (connectedMethods.addNewMember = addNewMember);

  const addOldMember = useCallback(() => {
    const id = 'existing_user';
    const item = setInitalItemProps(id);
    const newItems = insertInsideArray(
      [...items].filter((f) => f._id !== id),
      contextData.index + 1,
      item
    );
    setItems(newItems);
    setSelectedItem(item);
  }, [items, contextData?.index]);

  const onClear = useCallback(
    (id) => {
      if (id) {
        const newItems = items.filter((item) => item._id !== id);
        return setItems(newItems);
      }
    },
    [items]
  );
  return (
    <div className="table-container">
      <ContextMenu
        menuId={menuId}
        contextData={contextData}
        addNewMember={addNewMember}
        addOldMember={addOldMember}
        moveUp={moveUp}
        moveDown={moveDown}
        users={items}
        isStaff={tableData.isStaff}
      />
      <div className="bg-dark text-white d-flex align-items-center table-header">
        <h6 className="text-center w-100 m-0">{team.name}</h6>
      </div>
      <div className="judge-table table-responsive w-100" ref={table}>
        <table className="table">
          <thead>
            <tr className="text-center bg-light-gray" ref={tableHeaderRow}>
              {Object.keys(tableData.headers).map((h, i) => (
                <th
                  key={i}
                  style={headerTdStyle(tableData.headers[h], i)}
                  scope="col"
                  className={`table-header-td ${i === 0 ? 'first-cell' : ''}`}
                >
                  {tableData.headers[h].name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((user, i) => (
              <Row
                key={user._id}
                item={user}
                team={team}
                tableData={tableData}
                setSelectedItem={setSelectedItem}
                selectedItem={selectedItem}
                onClick={handleRowClick}
                handleContextMenu={handleContextMenu}
                index={i}
                onClear={onClear}
                calculatePositionOfStickyHeaders={
                  calculatePositionOfStickyHeaders
                }
                createUpdateItem={createUpdateItem}
                getItems={getItems}
                items={items}
                targetRole={targetRole}
                setItems={setItems}
                table={table}
                setInitalItemProps={setInitalItemProps}
                innerRef={(el) => (tableRows.current[i] = el)}
                createUpdateStatus={createUpdateStatus}
              />
            ))}
            <tr className="table-footer">
              <td className="first-cell" colSpan="11"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
